import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { JSX } from 'react';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EvaluationFormValues } from '../../../../library/models/evaluation';
import { fieldProps } from '../../../../library/formValidation';
import { AGE_BOUNDS, numericSlotProps, PAIN_LEVEL_BOUNDS } from '../../../../library/validationRules';
import ResourcesIds from '../../../../resources/ResourcesIds';
import { BodyDiagramField } from '../../../bodyDiagram/BodyDiagramField';
import { AppDatePicker } from '../../../pickers/AppDatePicker';
import {
    assessmentLayoutStyles,
    diagramBoxStyles,
    fieldGridStyles,
    hintStyles,
    narrowGridStyles,
    sectionTitleStyles,
    stackedFieldsStyles,
} from '../evaluation.styles';

interface AssessmentStepProps {
    register: UseFormRegister<EvaluationFormValues>;
    control: Control<EvaluationFormValues>;
    errors: FieldErrors<EvaluationFormValues>;
    /**
     * Whether the address is the one this sheet is already bound to.
     *
     * Set for a follow-up, where the patient was chosen from the list rather than typed:
     * the address is what attaches the record to an account, so editing it here would
     * quietly file the sheet against somebody else -- or invent them.
     */
    lockEmail?: boolean;
}

/**
 * Page one of the paper form: who the patient is, and what brought them in.
 *
 * The body diagram is where the therapist marks where it hurts. The marks are drawn into
 * the PDF and not kept on the record -- the signature's rule -- so a follow-up starts
 * with a blank figure.
 */
export const AssessmentStep = (props: AssessmentStepProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const { register, control, errors, lockEmail = false } = props;

    return (
        <Box>
            <Typography variant="h6" sx={sectionTitleStyles}>
                {translate(ResourcesIds.EVALUATION_PATIENT_SECTION)}
            </Typography>

            <Typography sx={hintStyles}>
                {translate(lockEmail
                    ? ResourcesIds.EVALUATION_EMAIL_LOCKED_HINT
                    : ResourcesIds.EVALUATION_EMAIL_HINT)}
            </Typography>

            <Box sx={fieldGridStyles}>
                <TextField
                    {...register('lastName')}
                    fullWidth
                    required
                    label={translate(ResourcesIds.LAST_NAME_LABEL)}
                    {...fieldProps(errors, 'lastName')}
                />
                <TextField
                    {...register('firstName')}
                    fullWidth
                    required
                    label={translate(ResourcesIds.FIRST_NAME_LABEL)}
                    {...fieldProps(errors, 'firstName')}
                />
                <TextField
                    {...register('emailAddress')}
                    fullWidth
                    required
                    type="email"
                    label={translate(ResourcesIds.EMAIL_LABEL)}
                    // readOnly rather than disabled: a disabled input is left out of the
                    // submission, and the address is the one field the API cannot do without.
                    slotProps={{ input: { readOnly: lockEmail } }}
                    {...fieldProps(errors, 'emailAddress')}
                />
                <TextField
                    {...register('phoneNumber')}
                    fullWidth
                    label={translate(ResourcesIds.PHONE_LABEL)}
                    {...fieldProps(errors, 'phoneNumber')}
                />
            </Box>

            <Box sx={{ ...narrowGridStyles, mt: 2 }}>
                <TextField
                    {...register('occupation')}
                    fullWidth
                    label={translate(ResourcesIds.EVALUATION_OCCUPATION_LABEL)}
                    {...fieldProps(errors, 'occupation')}
                />
                <TextField
                    {...register('age')}
                    fullWidth
                    type="number"
                    label={translate(ResourcesIds.EVALUATION_AGE_LABEL)}
                    slotProps={numericSlotProps(AGE_BOUNDS)}
                    {...fieldProps(errors, 'age')}
                />
                {/* Through a Controller, not `register` with a `defaultValue` of its own.
                    MUI's select is driven by its own state rather than by the DOM value
                    react-hook-form writes through the ref, so a hardcoded default here
                    silently outranks the form's -- which cost nothing while every
                    evaluation started blank, and drops the carried value on a follow-up. */}
                <Controller
                    name="sex"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            select
                            fullWidth
                            label={translate(ResourcesIds.EVALUATION_SEX_LABEL)}
                            {...fieldProps(errors, 'sex')}
                        >
                            <MenuItem value="">
                                {translate(ResourcesIds.EVALUATION_SEX_UNSPECIFIED)}
                            </MenuItem>
                            <MenuItem value="M">
                                {translate(ResourcesIds.EVALUATION_SEX_MALE)}
                            </MenuItem>
                            <MenuItem value="F">
                                {translate(ResourcesIds.EVALUATION_SEX_FEMALE)}
                            </MenuItem>
                        </TextField>
                    )}
                />
            </Box>

            <Typography variant="h6" sx={sectionTitleStyles}>
                {translate(ResourcesIds.EVALUATION_ASSESSMENT_SECTION)}
            </Typography>

            <Box sx={assessmentLayoutStyles}>
                <Box sx={stackedFieldsStyles}>
                    <Controller
                        name="evaluatedOn"
                        control={control}
                        render={({ field }) => (
                            <AppDatePicker
                                label={translate(ResourcesIds.EVALUATION_DATE_LABEL)}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                name={field.name}
                                inputRef={field.ref}
                                fullWidth
                                // An evaluation cannot have happened tomorrow.
                                disableFuture
                                {...fieldProps(errors, 'evaluatedOn')}
                            />
                        )}
                    />
                    <TextField
                        {...register('reasonForVisit')}
                        fullWidth
                        multiline
                        minRows={3}
                        label={translate(ResourcesIds.EVALUATION_REASON_LABEL)}
                        {...fieldProps(errors, 'reasonForVisit')}
                    />
                    <TextField
                        {...register('investigations')}
                        fullWidth
                        multiline
                        minRows={2}
                        label={translate(ResourcesIds.EVALUATION_INVESTIGATIONS_LABEL)}
                        {...fieldProps(errors, 'investigations')}
                    />
                    <TextField
                        {...register('painLevel')}
                        fullWidth
                        type="number"
                        label={translate(ResourcesIds.EVALUATION_PAIN_LEVEL_LABEL)}
                        slotProps={numericSlotProps(PAIN_LEVEL_BOUNDS)}
                        {...fieldProps(errors, 'painLevel')}
                    />
                    <TextField
                        {...register('painCharacter')}
                        fullWidth
                        multiline
                        minRows={2}
                        label={translate(ResourcesIds.EVALUATION_PAIN_CHARACTER_LABEL)}
                        {...fieldProps(errors, 'painCharacter')}
                    />
                    <TextField
                        {...register('medication')}
                        fullWidth
                        multiline
                        minRows={2}
                        label={translate(ResourcesIds.EVALUATION_MEDICATION_LABEL)}
                        {...fieldProps(errors, 'medication')}
                    />
                    <TextField
                        {...register('injuryHistory')}
                        fullWidth
                        multiline
                        minRows={2}
                        label={translate(ResourcesIds.EVALUATION_INJURY_HISTORY_LABEL)}
                        {...fieldProps(errors, 'injuryHistory')}
                    />
                    <TextField
                        {...register('notes')}
                        fullWidth
                        multiline
                        minRows={3}
                        label={translate(ResourcesIds.EVALUATION_NOTES_LABEL)}
                        {...fieldProps(errors, 'notes')}
                    />
                </Box>

                <Box sx={diagramBoxStyles}>
                    <Controller
                        name="bodyDiagram"
                        control={control}
                        render={({ field }): JSX.Element => (
                            <BodyDiagramField value={field.value} onChange={field.onChange} />
                        )}
                    />
                </Box>
            </Box>
        </Box>
    );
};
